if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        if (this._productFormInitialized) {
          if (this.classList.contains('product-form--pdp')) {
            this.ensurePdpCtaMarkup();
            this.bindSubscriptionPriceObserver();
          }
          return;
        }

        this.form = this.querySelector('form');
        if (!this.form) return;

        this._productFormInitialized = true;

        const variantInput = this.variantIdInput;
        if (variantInput) variantInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart =
          this.closest('cart-drawer') ||
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        if (!this.classList.contains('product-form--pdp')) return;

        this.ensurePdpCtaMarkup();
        this.bindSubscriptionPriceObserver();

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._pdpVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
            if (String(payload?.data?.sectionId) !== String(this.dataset.sectionId)) return;
            window.requestAnimationFrame(() => {
              this.ensurePdpCtaMarkup();
              this.bindSubscriptionPriceObserver();
            });
          });
        }
      }

      disconnectedCallback() {
        this._pdpVariantUnsubscriber?.();
        this.unbindSubscriptionPriceObserver();
      }

      unbindSubscriptionPriceObserver() {
        this._subscriptionPriceObserver?.disconnect();
        this._subscriptionPriceObserver = null;
        if (this._subscriptionChangeBound && this._subscriptionChangeRoot) {
          this._subscriptionChangeRoot.removeEventListener('change', this._subscriptionChangeBound);
          this._subscriptionChangeRoot.removeEventListener('click', this._subscriptionChangeBound);
        }
        this._subscriptionChangeBound = null;
        this._subscriptionChangeRoot = null;
        if (this._subscriptionBindTimer) {
          clearTimeout(this._subscriptionBindTimer);
          this._subscriptionBindTimer = null;
        }
      }

      ensurePdpCtaMarkup() {
        if (!this.classList.contains('product-form--pdp')) return;

        const btn = this.submitButton;
        if (!btn) return;

        let visible = btn.querySelector('.relab-card__cta-visible');
        if (!visible) {
          const legacy = btn.querySelector('span:not(.sold-out-message):not(.hidden)');
          if (!legacy) return;

          visible = document.createElement('span');
          visible.className = 'relab-card__cta-visible';
          const label = document.createElement('span');
          label.className = 'relab-card__cta-label';
          label.textContent = legacy.textContent.replace(/\s+/g, ' ').trim();
          visible.appendChild(label);
          legacy.replaceWith(visible);
        }

        let label = visible.querySelector('.relab-card__cta-label');
        if (!label) {
          label = document.createElement('span');
          label.className = 'relab-card__cta-label';
          label.textContent =
            visible.textContent.replace(/\s+/g, ' ').trim() ||
            window.variantStrings?.addToCart ||
            'Add to cart';
          visible.textContent = '';
          visible.appendChild(label);
        }

        if (!visible.querySelector('.relab-card__cta-sep')) {
          const sep = document.createElement('span');
          sep.className = 'relab-card__cta-sep';
          sep.setAttribute('aria-hidden', 'true');
          sep.textContent = '–';
          label.after(sep);
        }

        if (!visible.querySelector('.relab-card__cta-price')) {
          const price = document.createElement('span');
          price.className = 'relab-card__cta-price';
          const sep = visible.querySelector('.relab-card__cta-sep');
          (sep || label).after(price);
        }

        if (!visible.querySelector('.relab-card__cta-mid')) {
          const mid = document.createElement('span');
          mid.className = 'relab-card__cta-mid';
          mid.setAttribute('aria-hidden', 'true');
          mid.innerHTML =
            '<span class="relab-card__cta-line"></span><span class="relab-card__cta-dot"></span>';
          const price = visible.querySelector('.relab-card__cta-price');
          (price || label).after(mid);
        }

        const isSoldOut = btn.disabled || btn.getAttribute('aria-disabled') === 'true';
        if (isSoldOut) {
          visible.classList.add('relab-card__cta-visible--sold-out');
        } else {
          visible.classList.remove('relab-card__cta-visible--sold-out');
          visible
            .querySelectorAll('.relab-card__cta-sep, .relab-card__cta-price, .relab-card__cta-mid')
            .forEach((el) => el.classList.remove('hidden'));
          this.updateCtaPrice();
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.syncSellingPlanInput();
        const formData = new FormData(this.form);
        formData.delete('selling_plan');
        const sellingPlanId = this.resolveSellingPlanId();
        if (sellingPlanId) formData.set('selling_plan', sellingPlanId);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              const ctaVisible = this.submitButton.querySelector('.relab-card__cta-visible');
              if (ctaVisible) ctaVisible.classList.add('hidden');
              else this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const label = this.submitButton.querySelector('.relab-card__cta-label');
        const visible = this.submitButton.querySelector('.relab-card__cta-visible');
        const priceEl = this.submitButton.querySelector('.relab-card__cta-price');
        const sep = this.submitButton.querySelector('.relab-card__cta-sep');
        const mid = this.submitButton.querySelector('.relab-card__cta-mid');

        if (label) {
          if (disable) {
            this.submitButton.setAttribute('disabled', 'disabled');
            if (text) label.textContent = text;
            visible?.classList.add('relab-card__cta-visible--sold-out');
          } else {
            this.submitButton.removeAttribute('disabled');
            label.textContent = window.variantStrings.addToCart;
            visible?.classList.remove('relab-card__cta-visible--sold-out');
            this.ensurePdpCtaMarkup();
          }
          return;
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      updateCtaPrice() {
        const priceEl = this.submitButton?.querySelector('.relab-card__cta-price');
        if (!priceEl) return;

        const nextPrice = this.resolveDisplayedPrice();
        if (nextPrice) priceEl.textContent = nextPrice;
      }

      resolveDisplayedPrice() {
        const sectionId = this.dataset.sectionId;
        const priceRoot = sectionId ? document.getElementById(`price-${sectionId}`) : null;
        const scope = this.closest('product-info') || document;

        const isVisible = (el) => {
          if (!el || !el.isConnected) return false;
          if (el.closest('s')) return false;
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden';
        };

        const readText = (el) => (el?.textContent || '').replace(/\s+/g, ' ').trim();

        const sellingPlanSelected = Boolean(this.resolveSellingPlanId());

        // Appstle writes the live plan price here when a subscription is selected
        const appstleCandidates = [
          ...(priceRoot ? [...priceRoot.querySelectorAll('.appstle_subscription_final_price')] : []),
          ...scope.querySelectorAll('.appstle_subscription_final_price'),
        ];

        // Prefer a visible Appstle price; if a plan is selected, accept text even if
        // Appstle updated a node theme CSS currently hides (e.g. .price__regular on sale).
        let appstleFallback = '';
        for (const el of appstleCandidates) {
          if (el.closest('s')) continue;
          const text = readText(el);
          if (!text) continue;
          if (isVisible(el)) return text;
          if (!appstleFallback) appstleFallback = text;
        }
        if (sellingPlanSelected && appstleFallback) return appstleFallback;

        if (!priceRoot) return '';

        const fallback =
          priceRoot.querySelector('.price-item--sale:not(.appstle_subscription_final_price)') ||
          priceRoot.querySelector('.price-item--last') ||
          priceRoot.querySelector('.price__regular .price-item--regular') ||
          priceRoot.querySelector('.price-item--regular');

        return readText(fallback);
      }

      isSellingPlanId(value) {
        return Boolean(this.normalizeSellingPlanId(value));
      }

      normalizeSellingPlanId(value) {
        const v = String(value || '').trim();
        if (!v || v === '0' || /^one[-_]?time/i.test(v)) return '';
        const gid = v.match(/SellingPlan\/(\d+)/i);
        if (gid) return gid[1];
        if (/^\d+$/.test(v)) return v;
        return '';
      }

      resolveSellingPlanId() {
        const scope = this.closest('product-info') || document;
        const widget =
          scope.querySelector('[id^="appstle_subscription_widget"]') ||
          scope.querySelector('.appstle_sub_widget');

        const oneTimeChecked = Boolean(
          widget?.querySelector('.appstle_one_time_details_wrapper input[type="radio"]:checked') ||
            widget?.querySelector(
              '.appstle_subscription_wrapper_option.appstle-active-option .appstle_one_time_details_wrapper'
            )
        );
        const subscribeChecked = Boolean(
          widget?.querySelector(
            '.appstle_subscription_radio_wrapper input[type="radio"]:checked, ' +
              '.appstle_subscribe_option input[type="radio"]:checked'
          ) ||
            widget?.querySelector(
              '.appstle_subscription_wrapper_option.appstle-active-option .appstle_subscription_radio_wrapper'
            )
        );

        if (oneTimeChecked && !subscribeChecked) return '';

        if (widget && subscribeChecked) {
          const select =
            widget.querySelector('select.appstle_select') ||
            widget.querySelector('.appstle_custom_select select') ||
            widget.querySelector('select.appstle_custom_select') ||
            widget.querySelector('.appstle_subscribe_option select');
          const fromSelect = this.normalizeSellingPlanId(select?.value);
          if (fromSelect) return fromSelect;

          const subRadio = widget.querySelector(
            '.appstle_subscription_radio_wrapper input[type="radio"]:checked, ' +
              '.appstle_subscribe_option input[type="radio"]:checked'
          );
          const fromRadio = this.normalizeSellingPlanId(subRadio?.value);
          if (fromRadio) return fromRadio;
        }

        const fieldRoots = widget ? [widget, scope] : [scope];
        for (const root of fieldRoots) {
          const fields = [...root.querySelectorAll('[name="selling_plan"]')];
          for (const el of fields) {
            if (el === this._sellingPlanInput) continue;
            if (el.disabled) continue;
            if (el.type === 'radio' && !el.checked) continue;
            if (el.closest('.appstle_one_time_details_wrapper')) continue;
            const id = this.normalizeSellingPlanId(el.value);
            if (id) return id;
          }
        }

        return '';
      }

      ensureSellingPlanInput() {
        if (!this.form || !this.classList.contains('product-form--pdp')) return null;
        if (this._sellingPlanInput?.isConnected) return this._sellingPlanInput;

        let input = this.form.querySelector('input.product-selling-plan[name="selling_plan"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.className = 'product-selling-plan';
          this.form.appendChild(input);
        }
        this._sellingPlanInput = input;
        return input;
      }

      syncSellingPlanInput() {
        const input = this.ensureSellingPlanInput();
        if (!input) return;

        const planId = this.resolveSellingPlanId();
        input.value = planId;
        input.disabled = !planId;
      }

      bindSubscriptionPriceObserver() {
        if (!this.classList.contains('product-form--pdp')) return;

        this.unbindSubscriptionPriceObserver();

        const sectionId = this.dataset.sectionId;
        const priceRoot = sectionId ? document.getElementById(`price-${sectionId}`) : null;
        const productInfo = this.closest('product-info') || document;
        const appstleWidget =
          productInfo.querySelector('[id^="appstle_subscription_widget"]') ||
          productInfo.querySelector('.appstle_sub_widget') ||
          productInfo.querySelector('[class*="appstle"]');

        const targets = [priceRoot, appstleWidget].filter(Boolean);
        if (!targets.length) {
          // Appstle often injects after first paint — retry briefly
          this._subscriptionBindAttempts = (this._subscriptionBindAttempts || 0) + 1;
          if (this._subscriptionBindAttempts <= 6) {
            this._subscriptionBindTimer = setTimeout(() => this.bindSubscriptionPriceObserver(), 500);
          }
          return;
        }
        this._subscriptionBindAttempts = 0;

        const scheduleUpdate = () => {
          this.syncSellingPlanInput();
          window.requestAnimationFrame(() => this.updateCtaPrice());
        };

        this._subscriptionPriceObserver = new MutationObserver(scheduleUpdate);
        targets.forEach((target) => {
          this._subscriptionPriceObserver.observe(target, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'hidden'],
          });
        });

        this._subscriptionChangeBound = (event) => {
          const t = event.target;
          if (!t) return;
          if (
            t.name === 'selling_plan' ||
            t.closest?.(
              '[id^="appstle_subscription_widget"], .appstle_sub_widget, .appstle_select, .appstle_custom_select'
            )
          ) {
            // Appstle often updates the plan input a tick after the change event
            scheduleUpdate();
            setTimeout(scheduleUpdate, 50);
            setTimeout(scheduleUpdate, 200);
          }
        };
        this._subscriptionChangeRoot = productInfo;
        productInfo.addEventListener('change', this._subscriptionChangeBound);
        productInfo.addEventListener('click', this._subscriptionChangeBound);

        scheduleUpdate();
      }

      get submitButtonText() {
        return (
          this.submitButton?.querySelector('.relab-card__cta-label') || this.submitButton?.querySelector('span')
        );
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}
